/**
 * Token 管理器
 * 负责 JWT Token 的持久化存储、过期检查与自动刷新调度
 */

export interface TokenData {
  accessToken: string
  expiresAt: string
  refreshToken?: string
}

export class TokenManager {
  private refreshTimer: NodeJS.Timeout | null = null
  private readonly REFRESH_BEFORE_EXPIRY = 5 * 60 * 1000 // 提前5分钟刷新
  private readonly MIN_REFRESH_INTERVAL = 60 * 1000 // 最小刷新间隔1分钟
  
  /**
   * 保存token并启动自动刷新
   */
  public saveToken(accessToken: string, expiresAt: string, refreshToken?: string): void {
    localStorage.setItem('accessToken', accessToken)
    localStorage.setItem('tokenExpiresAt', expiresAt)
    
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken)
    }
    
    const expiryTime = new Date(expiresAt).getTime()
    this.scheduleRefresh(expiryTime)
  }
  
  /**
   * 调度token刷新
   */
  private scheduleRefresh(expiryTime: number): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
    
    const now = Date.now()
    const timeUntilExpiry = expiryTime - now
    const timeUntilRefresh = expiryTime - now - this.REFRESH_BEFORE_EXPIRY
    
    if (timeUntilExpiry <= this.MIN_REFRESH_INTERVAL || timeUntilRefresh <= this.MIN_REFRESH_INTERVAL) {
      this.refreshToken()
      return
    }
    
    this.refreshTimer = setTimeout(() => {
      this.refreshToken()
    }, timeUntilRefresh)
  }
  
  /**
   * 刷新token
   */
  public async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      
      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: refreshToken ? JSON.stringify({ refreshToken }) : undefined
      })
      
      if (!response.ok) {
        throw new Error('Token刷新失败')
      }
      
      const data = await response.json()
      this.saveToken(data.accessToken, data.expiresAt, data.refreshToken)
      this.dispatchTokenRefreshedEvent(data.accessToken)
      return true
    } catch (error) {
      this.dispatchTokenExpiredEvent()
      this.clearToken()
      return false
    }
  }
  
  /**
   * 清除token和定时器
   */
  public clearToken(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
    
    localStorage.removeItem('accessToken')
    localStorage.removeItem('tokenExpiresAt')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
  }
  
  public hasToken(): boolean {
    return !!localStorage.getItem('accessToken')
  }
  
  public getToken(): string | null {
    return localStorage.getItem('accessToken')
  }
  
  public isTokenExpiringSoon(): boolean {
    const expiresAt = localStorage.getItem('tokenExpiresAt')
    if (!expiresAt) return true
    const expiryTime = new Date(expiresAt).getTime()
    return (expiryTime - Date.now()) < this.REFRESH_BEFORE_EXPIRY
  }
  
  public isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem('tokenExpiresAt')
    if (!expiresAt) return true
    return Date.now() >= new Date(expiresAt).getTime()
  }
  
  public async forceRefresh(): Promise<boolean> {
    return await this.refreshToken()
  }
  
  public initialize(): void {
    const accessToken = localStorage.getItem('accessToken')
    const expiresAt = localStorage.getItem('tokenExpiresAt')
    
    if (!accessToken || !expiresAt) {
      return
    }
    
    const expiryTime = new Date(expiresAt).getTime()
    const now = Date.now()
    
    if (expiryTime <= now) {
      this.refreshToken()
    } else {
      this.scheduleRefresh(expiryTime)
    }
  }
  
  public getTokenRemainingTime(): number {
    const expiresAt = localStorage.getItem('tokenExpiresAt')
    if (!expiresAt) return 0
    const remaining = new Date(expiresAt).getTime() - Date.now()
    return Math.max(0, Math.floor(remaining / 1000))
  }
  
  private dispatchTokenRefreshedEvent(newToken: string): void {
    const event = new CustomEvent('token-refreshed', {
      detail: { accessToken: newToken }
    })
    window.dispatchEvent(event)
  }
  
  private dispatchTokenExpiredEvent(): void {
    const event = new Event('token-expired')
    window.dispatchEvent(event)
  }
}

export const tokenManager = new TokenManager()

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      if (tokenManager.hasToken() && tokenManager.isTokenExpired()) {
        tokenManager.refreshToken()
      }
    }
  })
}
